// src/pages/brand-collaborations-client-stories/components/BehindTheScenes.jsx
import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const BehindTheScenes = () => {
  const [activeProcess, setActiveProcess] = useState('integration');

  const processSteps = [
    {
      id: 'integration',
      title: 'Team Integration',
      icon: 'Users',
      description: 'Seamless collaboration with client teams',
      details: 'Our specialists integrate directly with your existing teams, understanding corporate protocols, communication styles, and decision-making processes to ensure smooth collaboration.',
      image: 'https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=1600',
      insights: [
        'Dedicated liaison for each department',
        'Corporate culture orientation sessions',
        'Customized communication protocols',
        'Regular stakeholder alignment meetings'
      ]
    },
    {
      id: 'adaptation',
      title: 'Cultural Adaptation',
      icon: 'Globe',
      description: 'Adapting to diverse corporate cultures',
      details: 'We study and adapt to your unique corporate culture, ensuring our creative approach aligns with your values, communication style, and brand personality while maintaining authenticity.',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=2070&auto=format&fit=crop',
      insights: [
        'Cultural sensitivity training for our team',
        'Brand voice and tone integration',
        'Corporate hierarchy respect',
        'Value alignment verification'
      ]
    },
    {
      id: 'consistency',
      title: 'Brand Consistency',
      icon: 'Target',
      description: 'Maintaining brand integrity across projects',
      details: 'Rigorous brand consistency protocols ensure every visual element, from color palettes to messaging tone, aligns perfectly with your established brand guidelines across all deliverables.',
      image: 'https://images.pixabay.com/photo/2017/08/10/02/05/people-2619817_960_720.jpg',
      insights: [
        'Comprehensive brand guideline review',
        'Color accuracy and typography consistency',
        'Message tone and voice verification',
        'Visual style guide adherence'
      ]
    },
    {
      id: 'quality',
      title: 'Quality Assurance',
      icon: 'Shield',
      description: 'Multi-layered quality control processes',
      details: 'Our quality assurance framework includes technical reviews, creative assessments, brand compliance checks, and client approval workflows to ensure exceptional deliverables.',
      image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=80&w=2070&auto=format&fit=crop',
      insights: [
        'Technical specification verification',
        'Creative excellence standards',
        'Brand compliance auditing',
        'Client feedback integration'
      ]
    }
  ];

  const activeStep = processSteps.find(step => step.id === activeProcess);

  const collaborationStats = [
    { metric: '99.8%', label: 'Client Approval Rate', icon: 'CheckCircle' },
    { metric: '2.5', label: 'Average Revisions', icon: 'RefreshCw' },
    { metric: '48h', label: 'Initial Draft Delivery', icon: 'Clock' },
    { metric: '100%', label: 'On-Time Delivery', icon: 'Target' }
  ];

  return (
    <section className="py-20 bg-background-dark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-text-primary-inverse mb-6">
            Behind the <span className="text-gradient-gold">Scenes</span>
          </h2>
          <p className="text-xl text-text-secondary-inverse max-w-3xl mx-auto leading-relaxed">
            Discover the collaborative process that enables seamless integration with corporate teams, 
            cultural adaptation, and consistent brand delivery across multiple projects
          </p>
        </div>

        {/* Process Navigation */}
        <div className="flex items-center justify-center mb-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl">
            {processSteps.map((step) => (
              <button
                key={step.id}
                onClick={() => setActiveProcess(step.id)}
                className={`p-4 rounded-lg text-center transition-all duration-300 ${
                  activeProcess === step.id
                    ? 'bg-accent text-white shadow-glow'
                    : 'bg-white/10 backdrop-blur-sm text-text-primary-inverse hover:bg-accent/20'
                }`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${
                  activeProcess === step.id ? 'bg-white/20' : 'bg-accent/20'
                }`}>
                  <Icon 
                    name={step.icon} 
                    size={24} 
                    color={activeProcess === step.id ? '#FFFFFF' : '#D4AF37'} 
                    strokeWidth={2} 
                  />
                </div>
                <h3 className="font-bold text-sm mb-1">{step.title}</h3>
                <p className="text-xs opacity-80">{step.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Active Process Content */}
        {activeStep && (
          <div className="animate-fade-in">
            <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
              {/* Process Image */}
              <div className="relative">
                <Image
                  src={activeStep.image}
                  alt={activeStep.title}
                  className="w-full h-96 object-cover rounded-lg"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent rounded-lg"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="text-white font-bold text-2xl mb-2">{activeStep.title}</h3>
                  <p className="text-white/80">{activeStep.description}</p>
                </div>
              </div>

              {/* Process Details */}
              <div>
                <h4 className="font-bold text-text-primary-inverse text-xl mb-4">Process Overview</h4>
                <p className="text-text-secondary-inverse text-lg leading-relaxed mb-6">
                  {activeStep.details}
                </p>
                
                <h4 className="font-bold text-text-primary-inverse text-lg mb-4">Key Insights</h4>
                <div className="space-y-3">
                  {activeStep.insights.map((insight, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-accent/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon name="Check" size={14} color="#D4AF37" strokeWidth={2} />
                      </div>
                      <span className="text-text-secondary-inverse">{insight}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Collaboration Statistics */}
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8">
          <h3 className="font-bold text-text-primary-inverse text-2xl mb-8 text-center">
            Collaboration Excellence Metrics
          </h3>
          <div className="grid md:grid-cols-4 gap-8">
            {collaborationStats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-accent/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={stat.icon} size={24} color="#D4AF37" strokeWidth={2} />
                </div>
                <div className="text-3xl font-bold text-accent mb-2">{stat.metric}</div>
                <div className="text-text-secondary-inverse text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Process Flow */}
        <div className="mt-16">
          <h3 className="font-bold text-text-primary-inverse text-2xl mb-8 text-center">
            Our Collaborative Process Flow
          </h3>
          <div className="relative">
            {/* Process Timeline */}
            <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-accent/30 transform -translate-y-1/2"></div>
            <div className="flex items-center justify-between relative z-10">
              {processSteps.map((step, index) => (
                <div key={step.id} className="flex flex-col items-center">
                  <div className={`w-12 h-12 rounded-full border-4 flex items-center justify-center mb-4 ${
                    index <= processSteps.findIndex(s => s.id === activeProcess)
                      ? 'bg-accent border-accent' :'bg-white/10 border-accent/30'
                  }`}>
                    <Icon 
                      name={step.icon} 
                      size={20} 
                      color={index <= processSteps.findIndex(s => s.id === activeProcess) ? '#FFFFFF' : '#D4AF37'} 
                      strokeWidth={2} 
                    />
                  </div>
                  <span className="text-text-secondary-inverse text-sm text-center max-w-20">
                    {step.title}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BehindTheScenes;